import * as React from 'react';
import * as classNames from 'classnames';
import * as GoogleOneTap from 'google-one-tap';
import {GOOGLE_CLIENT_ID} from '../../utils/config';
import './styles.sass';
import {guessTimezone} from '../../utils/time-zone-formatter';
import {getDistinctId} from '../../utils/tracker';

export interface IGoogleResponse extends GoogleOneTap.CredentialResponse {
}

export interface IGoogleAuthProps {
  disabled?: boolean;
  renderButton?: boolean;
  onSuccess?: (response: IGoogleResponse) => void;
  onFailure?: (response: any) => void;
}

const SRC = 'https://accounts.google.com/gsi/client';
const LOGIN_URL = `${location.origin}/user/google-one-tap`;
const BUTTON_RENDER_OPTIONS = {
  type: 'standard',
  size: 'medium',
  shape: 'rectangular',
  width: '302px', // Unfortunately only px values are accepted here, so this is based on the hardcoded value in .modal
};

const timezone = guessTimezone();
const distinctId = getDistinctId();

export const GoogleAuth: React.StatelessComponent<IGoogleAuthProps> = (
  {
    disabled,
    renderButton,
    onSuccess,
    onFailure,
  }: IGoogleAuthProps) => {
  const [scriptLoaded, setScriptLoaded] = React.useState(false);

  // Create a div reference only if we need to render a button
  const buttonDivRef = renderButton ? React.createRef<HTMLDivElement>() : null;

  // Only a "load" event from the script tag indicates that it's ready
  const eventToStatus = event => event.type === 'load' ? 'ready' : 'error';
  const setLoadingStatus = status => setScriptLoaded(status === 'ready');
  const eventToLoadingStatus = event => setLoadingStatus(eventToStatus(event));

  React.useEffect(() => {
    // The script might have already been rendered
    let script: HTMLScriptElement | null = document.querySelector(`script[src="${SRC}"]`);


    if (!script) {
      script = document.createElement('script');
      script.src = SRC;
      script.async = true;
      script.defer = true;


      // Store status in attribute on script, to be read by other instances of this component.
      // Only the instance that renders the script should update the corresponding attribute
      const setAttributeFromEvent = event => {
        !!script && script.setAttribute('data-status', eventToStatus(event));
      };
      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);

      document.querySelector('body').appendChild(script);
    } else {
      // Only when the src tag is ready should we assume the script is loaded
      setLoadingStatus(script.getAttribute('data-status') || 'idle');
    }

    // Add event listeners to reflect the script loading status
    script.addEventListener('load', eventToLoadingStatus);
    script.addEventListener('error', eventToLoadingStatus);

    // If the script was loaded successfully and a button needs to be rendered
    if (scriptLoaded && renderButton) {
      // initialize the library and render the button into the div ref
      const buttonInitOptions = {
        client_id: GOOGLE_CLIENT_ID,
        callback: (
          response => response && response.credential
            ? onSuccess(response)
            : onFailure(response)
        ),
      };

      const googleAccount = (window as any).google.accounts.id;
      googleAccount.initialize(buttonInitOptions);
      googleAccount.renderButton(buttonDivRef.current, BUTTON_RENDER_OPTIONS);
    }

    return () => {
      script.removeEventListener('load', eventToLoadingStatus);
      script.removeEventListener('error', eventToLoadingStatus);
    };
  }, [scriptLoaded]);

  if (!renderButton) {
    // If a button doesn't need to be rendered, this div would have the one-tap
    // iframe rendered within it as soon as the source script is loaded
    return <div id={'g_id_onload'}
                data-client_id={GOOGLE_CLIENT_ID}
                data-login_uri={LOGIN_URL}
                data-timezone={timezone}
                data-distinct_id={distinctId}
                data-terms_accepted={true}
                data-marketing_accepted={false}
                data-auto_select='true'
    />;
  }

  // Otherwise, we'll render an empty div into which we'll render an explicit
  // button as soon as we initialize the Google client
  return (
    <div
      ref={buttonDivRef}
      className={classNames('google-auth-component', {
        'google-auth-component_disabled': disabled,
      })}
    />
  );
};
